<template>
  <div class="matches-page">
    <div class="page-header">
      <h1>Match Results</h1>
      <p class="subtitle">Browse all completed matches</p>
    </div>

      <!-- Filters -->
      <div class="filters-section">
        <select v-model="selectedDivision" class="filter-select">
          <option value="">All Divisions</option>
          <option 
            v-for="division in divisions" 
            :key="division.value"
            :value="division.value"
          >
            {{ division.name }}
          </option>
        </select>
      </div>

      <!-- Matches Grid -->
      <div v-if="loading" class="loading-grid">
        <SkeletonCard v-for="i in 9" :key="i" />
      </div>

      <div v-else-if="error" class="error-message">
        <p>{{ error }}</p>
      </div>

      <div v-else-if="matches.length === 0" class="no-results">
        <p>No matches found.</p>
      </div>

      <!-- Show matches organized by division when "All Divisions" is selected -->
      <div v-else-if="!selectedDivision" class="divisions-container">
        <div 
          v-for="divisionGroup in matchesByDivision" 
          :key="divisionGroup.divisionId"
          class="division-section"
        >
          <h2 class="division-title">{{ divisionGroup.divisionName }}</h2>
          <div class="matches-grid">
            <div 
              v-for="match in divisionGroup.matches"
              :key="match.matchId"
              class="match-card"
              @click="goToMatch(match.matchId)"
            >
              <div class="match-card-header">
                <div class="header-content">
                  <span class="division-badge">{{ match.divisionName }}</span>
                  <span class="match-date">{{ formatDate(match.matchDate) }}</span>
                </div>
              </div>

              <div class="match-card-body">
                <div class="team" :class="{ winner: match.winner === match.teamOneId }">
                  <div class="team-name">{{ match.teamOneName }}</div>
                  <div class="team-score">{{ match.finalScore.teamOne }}</div>
                </div>

                <div class="vs-divider">VS</div>

                <div class="team" :class="{ winner: match.winner === match.teamTwoId }">
                  <div class="team-name">{{ match.teamTwoName }}</div>
                  <div class="team-score">{{ match.finalScore.teamTwo }}</div>
                </div>
              </div>

              <div class="match-card-footer">
                <span class="game-type">{{ match.gameType }}</span>
                <span class="rounds-count">{{ match.rounds.length }} Rounds</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Show regular grid when a specific division is selected -->
      <div v-else class="matches-grid">
        <div 
          v-for="match in matches"
          :key="match.matchId"
          class="match-card"
          @click="goToMatch(match.matchId)"
        >
          <div class="match-card-header">
            <div class="header-content">
              <span class="division-badge">{{ match.divisionName }}</span>
              <span class="match-date">{{ formatDate(match.matchDate) }}</span>
            </div>
          </div>

          <div class="match-card-body">
            <div class="team" :class="{ winner: match.winner === match.teamOneId }">
              <div class="team-name">{{ match.teamOneName }}</div>
              <div class="team-score">{{ match.finalScore.teamOne }}</div>
            </div>

            <div class="vs-divider">VS</div>

            <div class="team" :class="{ winner: match.winner === match.teamTwoId }">
              <div class="team-name">{{ match.teamTwoName }}</div>
              <div class="team-score">{{ match.finalScore.teamTwo }}</div>
            </div>
          </div>

          <div class="match-card-footer">
            <span class="game-type">{{ match.gameType }}</span>
            <span class="rounds-count">{{ match.rounds.length }} Rounds</span>
          </div>
        </div>
      </div>

      <!-- Load More -->
      <div v-if="matches.length > 0 && canLoadMore" class="load-more-section">
        <button @click="loadMore" class="load-more-btn">
          Load More Matches
        </button>
      </div>
  </div>
</template>

<script setup lang="ts">
import { collection, getDocs, query, where, orderBy, limit, type QueryConstraint } from 'firebase/firestore';

const router = useRouter();

const selectedDivision = ref('');
const currentLimit = ref(20);

const matches = ref<any[]>([]);
const loading = ref(true);
const error = ref<string | null>(null);

// Divisions list - will be populated dynamically from matches
const divisions = ref<Array<{ value: string; name: string }>>([]);

const canLoadMore = computed(() => matches.value.length >= currentLimit.value);

// Group matches by division for better visualization
const matchesByDivision = computed(() => {
  if (selectedDivision.value) return [];
  
  const grouped = new Map<string, { divisionId: string; divisionName: string; matches: any[] }>();
  
  matches.value.forEach(match => {
    const divisionId = match.divisionId;
    const divisionName = match.divisionName;
    
    if (!grouped.has(divisionId)) {
      grouped.set(divisionId, {
        divisionId,
        divisionName,
        matches: []
      });
    }
    
    grouped.get(divisionId)!.matches.push(match);
  });
  
  // Convert to array and sort by division name
  return Array.from(grouped.values()).sort((a, b) => 
    a.divisionName.localeCompare(b.divisionName)
  );
});

const LEAGUE_ID = 'abdd3e6f-55de-4d1f-8be0-b2d40139058f';

onMounted(async () => {
  console.log('Matches page mounted');
  try {
    // Wait a bit for Firebase to initialize
    await new Promise(resolve => setTimeout(resolve, 200));
    await loadMatches();
  } catch (e: any) {
    console.error('Error in onMounted:', e);
    error.value = e.message;
  }
});

async function loadMatches() {
  loading.value = true;
  error.value = null;
  
  try {
    const { $firebase } = useNuxtApp();
    const db = ($firebase as any).firestore;
    
    console.log('Loading matches from Firestore...');
    console.log('League ID:', LEAGUE_ID);
    console.log('Filters:', {
      division: selectedDivision.value || 'All',
      limit: currentLimit.value
    });
    
    const matchesRef = collection(db, `leagueStandings/${LEAGUE_ID}/matchResults`);
    const constraints: QueryConstraint[] = [];

    // Apply division filter if selected
    if (selectedDivision.value) {
      console.log('Adding division filter:', selectedDivision.value);
      constraints.push(where('divisionId', '==', selectedDivision.value));
      // Order by date (most recent first)
      constraints.push(orderBy('matchDate', 'desc'));
    } else {
      // When no filter, just order by date
      constraints.push(orderBy('matchDate', 'desc'));
    }

    // Limit results
    constraints.push(limit(currentLimit.value));

    console.log('Building query with', constraints.length, 'constraints');
    const q = query(matchesRef, ...constraints);
    
    console.log('Executing query...');
    const snapshot = await getDocs(q);
    
    console.log('Query complete. Documents found:', snapshot.docs.length);

    matches.value = snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        matchId: doc.id,
        ...data
      };
    });
    
    console.log('Matches loaded:', matches.value.length);
    if (matches.value.length > 0) {
      console.log('First match sample:', matches.value[0]);
    }

    // Extract unique divisions from matches (only when loading all divisions)
    if (!selectedDivision.value && matches.value.length > 0) {
      const uniqueDivisions = new Map<string, string>();
      matches.value.forEach(match => {
        if (match.divisionId && match.divisionName) {
          uniqueDivisions.set(match.divisionId, match.divisionName);
        }
      });
      
      divisions.value = Array.from(uniqueDivisions.entries())
        .map(([id, name]) => ({ value: id, name }))
        .sort((a, b) => a.name.localeCompare(b.name));
      
      console.log('Divisions extracted:', divisions.value);
    }

  } catch (e: any) {
    console.error('Error loading matches:', {
      message: e.message,
      code: e.code,
      name: e.name
    });
    error.value = e.message;
    
    if (e.code === 'permission-denied') {
      console.error('⚠️ PERMISSION DENIED - Check Firestore rules');
    }
  } finally {
    loading.value = false;
  }
}

async function loadMore() {
  currentLimit.value += 20;
  await loadMatches();
}

// Watch for filter changes and apply automatically (skip initial mount)
watch(selectedDivision, async (newValue, oldValue) => {
  // Skip if this is the initial value (oldValue is undefined on mount)
  if (oldValue === undefined) {
    console.log('Skipping initial watch trigger');
    return;
  }
  
  console.log('Division filter changed from', oldValue, 'to', newValue);
  currentLimit.value = 20;
  await loadMatches();
});

function goToMatch(matchId: string) {
  router.push(`/match/${matchId}`);
}

function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
}

// SEO
useHead({
  title: 'Match Results - The Players League DFW',
  meta: [
    {
      name: 'description',
      content: 'Browse all completed match results for The Players League DFW. View detailed round-by-round scores, team performances, and match history.'
    }
  ]
});
</script>

<style scoped>
.matches-page {
  min-height: 100vh;
  padding: 2rem 1rem;
  max-width: 1400px;
  margin: 0 auto;
  background: linear-gradient(180deg, #0f172a 0%, #1e3a8a 100%);
}

.page-header {
  text-align: center;
  margin-bottom: 3rem;
  color: white;
}

.page-header h1 {
  font-size: 2.5rem;
  margin-bottom: 0.5rem;
  color: var(--gold, #fbbf24);
}

.subtitle {
  color: rgba(255, 255, 255, 0.7);
  font-size: 1.125rem;
}

.filters-section {
  display: flex;
  gap: clamp(0.5rem, 1.5vw, 1rem);
  margin-bottom: clamp(1.5rem, 2.5vw, 2rem);
  flex-wrap: wrap;
  align-items: center;
}

.filter-select {
  padding: clamp(0.5rem, 1vw, 0.75rem) clamp(0.75rem, 1.5vw, 1rem);
  border: 1px solid rgba(251, 191, 36, 0.3);
  border-radius: clamp(0.375rem, 0.8vw, 0.5rem);
  background: rgba(255, 255, 255, 0.05);
  color: white;
  font-size: clamp(0.8rem, 1.2vw + 0.3rem, 1rem);
  cursor: pointer;
  backdrop-filter: blur(10px);
  min-width: 0;
  max-width: 100%;
}

.filter-select option {
  background: var(--navy, #1e3a8a);
  color: white;
}

.divisions-container {
  display: flex;
  flex-direction: column;
  gap: 3rem;
}

.division-section {
  width: 100%;
}

.division-title {
  font-size: 1.75rem;
  color: var(--gold, #fbbf24);
  margin-bottom: 1.5rem;
  padding-bottom: 0.75rem;
  border-bottom: 2px solid rgba(251, 191, 36, 0.3);
  font-weight: 600;
}

.matches-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
}

.match-card {
  background: rgba(255, 255, 255, 0.05);
  border-radius: clamp(0.75rem, 1.5vw, 1rem);
  padding: clamp(1rem, 2vw, 1.5rem);
  cursor: pointer;
  transition: all 0.3s;
  border: 1px solid transparent;
  backdrop-filter: blur(10px);
}

.match-card:hover {
  border-color: var(--gold, #fbbf24);
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
}

.match-card-header {
  margin-bottom: clamp(0.75rem, 1.5vw, 1rem);
}

.header-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: clamp(0.35rem, 0.8vw, 0.5rem);
}

.division-badge {
  font-size: clamp(0.7rem, 1.2vw + 0.3rem, 0.875rem);
  padding: clamp(0.2rem, 0.5vw, 0.25rem) clamp(0.5rem, 1vw, 0.75rem);
  background: var(--gold, #fbbf24);
  color: var(--navy, #1e3a8a);
  border-radius: 999px;
  font-weight: 600;
}

.match-date {
  font-size: clamp(0.7rem, 1.2vw + 0.3rem, 0.875rem);
  color: rgba(255, 255, 255, 0.6);
  text-align: center;
}

.match-card-body {
  margin-bottom: clamp(0.75rem, 1.5vw, 1rem);
  color: white;
}

.team {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: clamp(0.5rem, 1vw, 0.75rem) 0;
  gap: clamp(0.5rem, 1vw, 1rem);
}

.team.winner .team-name {
  font-weight: 700;
  color: var(--gold, #fbbf24);
}

.team-name {
  font-size: clamp(0.9rem, 1.5vw + 0.4rem, 1.125rem);
  line-height: 1.3;
}

.team-score {
  font-size: clamp(1.5rem, 2.5vw + 0.5rem, 2rem);
  font-weight: bold;
  color: var(--gold, #fbbf24);
}

.vs-divider {
  text-align: center;
  color: rgba(255, 255, 255, 0.5);
  font-weight: bold;
  margin: clamp(0.25rem, 0.8vw, 0.5rem) 0;
  font-size: clamp(0.7rem, 1.2vw + 0.3rem, 0.875rem);
}

.match-card-footer {
  display: flex;
  justify-content: space-between;
  font-size: clamp(0.7rem, 1.2vw + 0.3rem, 0.875rem);
  color: rgba(255, 255, 255, 0.6);
  border-top: 1px solid rgba(251, 191, 36, 0.2);
  padding-top: clamp(0.75rem, 1.5vw, 1rem);
}

.load-more-section {
  text-align: center;
  margin-top: 2rem;
}

.load-more-btn {
  padding: 1rem 2rem;
  background: var(--gold, #fbbf24);
  color: var(--navy, #1e3a8a);
  border: none;
  border-radius: 0.5rem;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s;
}

.load-more-btn:hover {
  background: #f59e0b;
  transform: translateY(-2px);
}

.loading-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 1.5rem;
}

.error-message,
.no-results {
  text-align: center;
  padding: 3rem 1rem;
  color: rgba(255, 255, 255, 0.7);
  font-size: 1.125rem;
}

.error-message {
  color: #ef4444;
}

@media (max-width: 768px) {
  .matches-grid,
  .loading-grid {
    grid-template-columns: 1fr;
  }

  .page-header h1 {
    font-size: 2rem;
  }
}
</style>
